import { TFunction } from 'i18next';
import { useTranslation as originalUseTranslation } from 'react-i18next';

import { createFallbackTranslation } from './translations';

/**
 * Hook that provides an enhanced translation function with automatic fallback
 * This hook overrides the default `t` function to automatically use fallback translations
 * when keys are missing.
 *
 * Usage: const { t } = useTranslation();
 *
 * @returns Enhanced translation function and other i18n utilities
 */
export const useTranslation = () => {
  const { t: originalT, ...rest } = originalUseTranslation();

  const tWithFallback = ((key: string, options?: Record<string, unknown>) => {
    const result = originalT(key, options);

    // If the result is the same as the key, it means the translation is missing
    if (result === key) {
      return createFallbackTranslation(key);
    }

    return result;
  }) as TFunction;

  return {
    t: tWithFallback,
    ...rest,
  };
};
